import type { BrandConfigInput } from "@cfg/brand-sdk";

/**
 * BrickBrackets, dark edition of the brand kit: an Ink (#17202E) bench with
 * lighter ink panels and hairline borders, Stud Orange (#FF5A1F) as the one
 * accent the 3D model shares, Stud Yellow (#FFB81C) only for print-check
 * warnings, Paper (#F7F2E8) for text. Poppins for words, JetBrains Mono for
 * every millimetre.
 * The palette is dark by design, so there are no light/dark variants; the
 * overrides set `color-scheme: dark` so native controls follow.
 * Serializable: this object becomes brand.json at deploy.
 */
export const config: BrandConfigInput = {
  id: "brickbrackets",
  name: "BrickBrackets",
  tagline: "Parametric display brackets for LEGO sets, generated in your browser.",
  domain: "brickbrackets.io",
  locale: "en",
  scene: "workshop",
  packs: ["brickbrackets@^1"],
  catalog: { include: ["brickbrackets.*"] },
  features: { betaChannel: false, fitTags: true, publicGallery: true },
  fitTags: {
    label: "LEGO® set",
    refPattern: "^\\d{3,7}(-\\d{1,2})?$",
    refPlaceholder: "e.g. 42115-1",
    namePlaceholder: "Set name (optional)",
    disclaimer: "LEGO® is a trademark of the LEGO Group, which does not sponsor, authorize or endorse this site.",
  },
  meta: {
    description: "Parametric display brackets for LEGO® sets. Set the dimensions, check the print, download the STL. Generated in your browser.",
    icons: { ico: "/favicon.ico", svg: "/icon.svg", appleTouch: "/apple-touch-icon.png" },
    manifest: "/site.webmanifest",
    ogImage: { url: "/og-image.png", width: 1200, height: 630, alt: "BrickBrackets.io" },
  },
  copy: {
    "nav.catalog": "Brackets",
    "nav.mine": "My brackets",
    "catalog.title": "Pick a bracket",
    "catalog.eyebrow": "Stud-accurate · 8 mm grid",
    "catalog.empty": "No brackets published yet.",
    "configurator.params": "Dimensions",
    "configurator.warnings": "Print check",
    "configurator.namePlaceholder": "e.g. Sián wheel display",
    "mine.title": "My brackets",
    "mine.empty": "Nothing saved yet. Configure a bracket and press Save.",
    "mine.filter": "Filter by set number",
    "community.title": "Community brackets",
    "community.sub": "Dimensions other builders printed, tagged by the set they fit.",
    "community.searchLabel": "Set number or name",
    "community.searchAction": "Search",
    "community.designLabel": "Bracket",
    "community.allDesigns": "All brackets",
    "community.noMatch": "No brackets tagged {ref} yet.",
    "community.noMatchDesign": "Nothing published for this bracket yet.",
    "community.noMatchBody": "Configure one, print it and tag it with the set, and it shows up here.",
    "community.pickDesign": "Pick a bracket",
    "community.params": "Dimensions",
    "community.browse": "Browse community brackets",
    "share.missingBody": "Ask for a new link, or start from a bracket.",
    "share.browse": "Browse brackets",
    "fits.printed": "I've printed this and it fits my set",
    "footer.madeWith": "Every millimetre is computed in your browser. Nothing leaves your machine until you press Save.",
  },
  theme: {
    // WCAG contrast on bg/surface/raised/panel:
    // text 14.7/13.8/12.6/14.0, textMuted 8.0/7.6/6.9/7.6, accent-text (#FF7A47) 6.3/6.0/5.4/6.0,
    // warn 9.5/8.9/8.1/9.0, danger 6.0/5.7/5.2/5.7, success 8.6/8.1/7.4/8.2; Ink on the orange button 5.3;
    // input borders (#6B768A) 3.4 on panel, 3.9 on field.
    colors: {
      bg: "#17202E",
      surface: "#1B2535",
      surfaceRaised: "#212C3E",
      text: "#F7F2E8",
      textMuted: "#AEB6C4",
      accent: "#FF5A1F",
      accentContrast: "#17202E",
      accentSoft: "rgba(255,90,31,0.16)",
      border: "#2B3546",
      borderStrong: "#3A4458",
      danger: "#FF6B86",
      success: "#43D394",
      model: "#FF5A1F",
      stage: "#141C29",
    },
    fonts: {
      display: '"Poppins", ui-sans-serif, system-ui, sans-serif',
      body: '"Poppins", ui-sans-serif, system-ui, sans-serif',
      mono: '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
    },
    webFonts: ["Poppins:wght@400;500;600;700", "JetBrains Mono:wght@400;500"],
    radius: { sm: "8px", md: "12px", lg: "18px", pill: "999px" },
    spacing: { unit: 4 },
    type: { baseSize: "14px", ratio: 1.2, labelTracking: "0.02em" },
    shadows: {
      sm: "0 1px 2px rgba(5,9,16,0.45)",
      md: "0 8px 24px -8px rgba(5,9,16,0.6), 0 2px 6px rgba(5,9,16,0.35)",
      lg: "0 24px 60px -16px rgba(5,9,16,0.75), 0 4px 12px rgba(5,9,16,0.4)",
    },
    extras: {
      glass: "rgba(23,32,46,0.74)",
      hairline: "rgba(221,226,234,0.08)",
      highlight: "rgba(221,226,234,0.05)",
      grid: "rgba(221,226,234,0.14)",
      glow: "rgba(255,90,31,0.35)",
      ease: "cubic-bezier(0.2, 0.8, 0.2, 1)",
      // Orange as text (links, active labels): the kit's lighter orange, readable on every ink surface.
      "accent-text": "#FF7A47",
      // Stud Yellow, the secondary accent: print-check warnings and clamp hints only.
      warn: "#FFB81C",
    },
  },
};
